import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { ProjectStatusId } from 'app/shared/data/project-enums';
import { FenceType } from '../../models/fence-type';
import { LinerType } from '../../models/liner-type';
import { PermitType } from '../../models/permit-type';
import { PondSize } from '../../models/pond-size';
import { ProjectPondParameter } from '../../models/project-pond-parameter';
import { WaterType } from '../../models/water-type';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { TableLookupService } from '../../services/lookups/table-lookup.service';
import { ProjectModuleService } from '../../services/project-module.service';

@Component({
  selector: 'app-project-pond-build',
  templateUrl: './project-pond-build.component.html',
  styleUrls: ['./project-pond-build.component.scss'],
  imports: [
    ComboBoxComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    TableLookupService,
  ],
})
export class ProjectPondBuildComponent implements OnInit {
  private lookupService = inject(TableLookupService);
  private lookupPlusService = inject(LookupPlusService);
  private projectModuleService = inject(ProjectModuleService);
  private fb = inject(UntypedFormBuilder);

  private tPermitType: string = 'PermitTypes';
  private tFenceType: string = 'FenceTypes';
  private tPondSize: string = 'PondSizes';
  private tLinerType: string = 'LinerTypes';
  private tWaterType: string = 'WaterTypes';
  private tProjectPondParameter: string = 'ProjectPondParameters';
  private expand: string = '$expand=FenceType%2C%20LinerType%2C%20PermitType%2C%20PondSize%2C%20WaterType';
  private filter: string = '&$filter=ProjectId%20eq%20';
  private odataUrl: string;
  private idProjectPondParameter: number;

  public oProjectPondParameter: ProjectPondParameter;
  public dPermitType = Array<PermitType>();
  public dWaterType = Array<WaterType>();
  public dLinerType = Array<LinerType>();
  public dFenceType = Array<FenceType>();
  public dPondSize = Array<PondSize>();
  public plexForm: UntypedFormGroup;
  public isFormReady: boolean;

  readonly iProjectId = input<number>(undefined);
  readonly canEdit = input<boolean>(undefined);
  readonly iProjectStatusId = input<number>(undefined);

  // emitter tabTdv provides touched-dirty-valid status of the form in this tab
  readonly tabTdv = output<boolean>();

  private loadPageData() {
    if (this.iProjectId() !== null && this.iProjectId() !== undefined) {
      this.odataUrl = `${this.expand}${this.filter}${this.iProjectId()}`;
      this.getPondParameters(this.tProjectPondParameter, this.odataUrl);
    }
  }

  private getPondParameters(table: string, o: string) {
    this.lookupPlusService.getLookup(table, o)
      .then(payload => {
        if (payload.value.length > 1) {
          console.warn('more than one set of pond parameters for this project');
        }
        this.oProjectPondParameter = Object.assign({}, payload.value[0]);
        this.idProjectPondParameter = this.oProjectPondParameter.ProjectPondParameterId;
        this.populateForm();
      });
  }

  private getComboBoxData<T>(table: string, data: Array<T>): void {
    this.lookupService.getLookup(table)
      .then((payload) => {
        for (let p in payload.value) {
          if (payload.value[p]) {
            data.push(payload.value[p]);
          }
        }
      });
  }

  private generateForm(): void {
    this.plexForm = this.fb.group({
      WaterType: [null, [Validators.required]],
      PermitType: [null, [Validators.required]],
      PondSize: [null, [Validators.required]],
      FenceType: [null, [Validators.required]],
      LinerType: [null, [Validators.required]]
    });

    this.plexForm.valueChanges.subscribe(data => this.onValueChanged(data));
  }

  private onValueChanged(data?: any) {
    if (!this.plexForm) {
      return;
    }
    let isValid: boolean = false;
    if (this.plexForm.valid && this.plexForm.dirty) {
      isValid = true;
      this.isFormReady = true;
      this.setTDV();
    } else {
      isValid = false;
      this.isFormReady = false;
      this.clearTDV();
    }
  }

  private populateForm(): void {
    this.isFormReady = false;
    this.plexForm.reset();
    this.plexForm.patchValue({
      WaterType: this.oProjectPondParameter.WaterType,
      PermitType: this.oProjectPondParameter.PermitType,
      PondSize: this.oProjectPondParameter.PondSize,
      FenceType: this.oProjectPondParameter.FenceType,
      LinerType: this.oProjectPondParameter.LinerType
    });
    if (!this.canEdit()) {
      this.plexForm.disable();
    }
  }

  ngOnInit() {
    this.getComboBoxData<PermitType>(this.tPermitType, this.dPermitType);
    this.getComboBoxData<LinerType>(this.tLinerType, this.dLinerType);
    this.getComboBoxData<WaterType>(this.tWaterType, this.dWaterType);
    this.getComboBoxData<FenceType>(this.tFenceType, this.dFenceType);
    this.getComboBoxData<PondSize>(this.tPondSize, this.dPondSize);

    this.generateForm();
    if (this.iProjectStatusId() === ProjectStatusId.Closed || this.iProjectStatusId() === ProjectStatusId.Cancelled || !this.canEdit()) {
      this.plexForm.disable();
    }
    this.loadPageData();
  }

  public cancelChanges(e): void {
    this.loadPageData();
    this.isFormReady = false;
  }

  public updateProjectPondParameters(e): void {
    let updatePacket: any = {
      FenceTypeId: this.plexForm.controls.FenceType.value.FenceTypeId,
      PermitTypeId: this.plexForm.controls.PermitType.value.PermitTypeId,
      LinerTypeId: this.plexForm.controls.LinerType.value.LinerTypeId,
      WaterTypeId: this.plexForm.controls.WaterType.value.WaterTypeId,
      PondSizeId: this.plexForm.controls.PondSize.value.PondSizeId,
    };
    //  Get PATCH request in form that's usable with the api
    let Form = JSON.stringify(updatePacket);

    this.projectModuleService.update(this.idProjectPondParameter, updatePacket, this.tProjectPondParameter)
      .then(() => {
        this.loadPageData();
      }
      );
  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }
}